import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../lib/api.js';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'none';

export type Agency = {
  id: string;
  name: string;
  slug: string;
  subscription_status: SubscriptionStatus;
  subscription_plan: 'monthly' | 'annual' | 'none';
  is_comped: boolean;
  current_period_end: string | null;
  active_seats: number;
  // Super-admin free-access window — full access while in the future (gate honours it live).
  trial_ends_at: string | null;
  // Suspension (independent of billing). suspended_at set → all users blocked.
  suspended_at: string | null;
  suspend_reason: string | null;
  // Pending admin setup invite (a hand-created agency before its admin
  // accepts) — drives the "Resend invite" action. null once accepted / none.
  pending_admin_invite_email: string | null;
  pending_admin_invite_expires_at: string | null;
  // Active admins in the tenant. 0 → nobody inside can run it; the panel shows
  // the red "No active admin" badge + the Invite-admin rescue action. Optional
  // so fixtures that predate the field still compile; absence hides the badge.
  active_admin_count?: number;
};

export function useAgencies() {
  return useQuery({
    queryKey: ['admin', 'agencies'],
    queryFn: () => apiGet<{ agencies: Agency[] }>('/admin/agencies'),
    staleTime: 30_000,
  });
}

export function useSetComp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isComped }: { id: string; isComped: boolean }) =>
      apiPatch<{ agency: Agency }>(`/admin/agencies/${id}/comp`, { is_comped: isComped }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

/** Resend a hand-created agency's pending admin setup invite (rotates the token). */
export function useResendAgencyInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ email: string; expires_at: string }>(`/admin/agencies/${id}/resend-invite`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

/** Invite a first admin into an existing tenant (zero-admin rescue; role is fixed to admin). */
export function useInviteAgencyAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      apiPost<{ email: string; expires_at: string }>(`/admin/agencies/${id}/invite-admin`, {
        email,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

/** Super-admin creates an agency by hand; the admin sets their own password via invite. */
export function useCreateAgency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { agency_name: string; admin_full_name: string; email: string }) =>
      apiPost<{ agency: Agency }>('/admin/agencies', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

/** Set / extend / clear an agency's trial window. ISO date string, or null to clear. */
export function useSetTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, trialEndsAt }: { id: string; trialEndsAt: string | null }) =>
      apiPatch<{ agency: Agency }>(`/admin/agencies/${id}/trial`, { trial_ends_at: trialEndsAt }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

/** Suspend an agency — immediately blocks all its users. Optional internal reason. */
export function useSuspendAgency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiPost<{ agency: Agency }>(`/admin/agencies/${id}/suspend`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

/** Reactivate a suspended agency — hands control back to the billing gate. */
export function useReactivateAgency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ agency: Agency }>(`/admin/agencies/${id}/reactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

// ---- Self-serve agency signup review (Phase 10, Stage 2) ----

export type AgencySignup = {
  id: string;
  agency_name: string;
  requested_slug: string;
  admin_email: string;
  admin_full_name: string;
  email_verified_at: string | null;
  created_at: string;
};

/**
 * Verified applications awaiting super-admin review. Disabled for non-super
 * admins (the Sidebar calls this on every page; pass `enabled: false` there).
 * Key nests under ['admin','agencies'] so the comp/approve/reject mutations'
 * prefix-invalidation refreshes this list too.
 */
export function useAgencySignups(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'agencies', 'signups'],
    queryFn: () => apiGet<{ signups: AgencySignup[] }>('/admin/agencies/signups'),
    staleTime: 30_000,
    enabled,
  });
}

export function useApproveSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ signup: unknown }>(`/admin/agencies/signups/${id}/approve`),
    // Prefix-invalidate ['admin','agencies'] → refreshes the pending list, the
    // tenants table (new tenant appears), and the sidebar count together.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}

export function useRejectSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiPost<{ signup: unknown }>(`/admin/agencies/signups/${id}/reject`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'agencies'] }),
  });
}
